/**
 * Extracts the SoundGuys editorial review out of a product line's enrichment.
 *
 * DealHunt returns the SoundGuys score + sub-ratings + MDAQS in `enrichment.ratingsBySource`,
 * the verdict overview in `enrichment.verdicts` (per source), and pros/cons in
 * `enrichment.prosBySource`/`consBySource` (one bulleted string per source). This builder
 * pulls the SoundGuys-sourced entries ONLY — every `*BySource` collection aggregates every
 * publication DealHunt holds content from, so a non-SoundGuys entry reaching this model
 * would be rendered under a SoundGuys heading and byline. Absent fields stay null/empty and
 * the renderer collapses the corresponding sub-block; there is no substitute copy.
 */
import type {
  DealHuntContentGroup,
  DealHuntProductLine,
  DealHuntRatingGroup,
  ReviewPrice,
  ReviewRival,
} from './types'
import { dealHuntProductHref, productDisplayName } from './url'

export interface ReviewSubScore {
  key: string
  label: string
  /** Normalized to a /10 scale for display. */
  score: number
  /** 0–100 bar fill. */
  pct: number
}

export interface MdaqsScore {
  key: string
  label: string
  /** On the source scale (MDAQS is /5). */
  score: number
  scale: number
  pct: number
}

/** One byline author for the review block (a product can have several). */
export interface ReviewAuthor {
  name: string
  initials: string
  avatarUrl: string | null
  profileUrl: string | null
  position: string | null
}

export interface SoundGuysReviewModel {
  /** Overall score, /10. */
  score: number | null
  verdictLabel: string | null
  /** All credited reviewers — DealHunt sends the full author list per review. */
  reviewers: ReviewAuthor[]
  /** Human-facing date, e.g. "Reviewed May 2025". */
  reviewDate: string | null
  /** Raw ISO publish timestamp — what schema.org `datePublished` needs. Null when
   *  DealHunt has no date for the review. Kept separate from the formatted
   *  `reviewDate` so structured data never has to re-parse display text. */
  publishedAt: string | null
  reviewUrl: string | null
  subScores: ReviewSubScore[]
  mdaqs: MdaqsScore[]
  /** Editorial verdict summary (null when the API hasn't supplied one). */
  summary: string | null
  pros: string[]
  cons: string[]
  /** Sponsored SoundGuys post ("Promoted") serving as the review — the section labels it
   *  and never calls it a review in copy or structured data. */
  promoted: boolean
  sponsorName: string | null
}

// Curated label + display order for the sub-rating bars. Iterated in order; the
// first label that has a matching rating wins (so we never show "Noise cancelling"
// twice when both `active_noise_cancelling` and `noise_cancelling` are present).
const SUB_SCORE_LABELS: [type: string, label: string][] = [
  ['sound_quality', 'Sound quality'],
  ['active_noise_cancelling', 'Noise cancelling'],
  ['noise_cancelling', 'Noise cancelling'],
  ['isolation_attenuation', 'Isolation'],
  ['battery_life', 'Battery life'],
  ['comfort', 'Comfort & fit'],
  ['connectivity', 'Connectivity'],
  ['build_quality', 'Build quality'],
  ['design', 'Design'],
  ['portability', 'Portability'],
  ['feature', 'Features'],
  ['value', 'Value'],
]

const MDAQS_LABELS: Record<string, string> = {
  mdaqs_timbre: 'Timbre',
  mdaqs_distortion: 'Distortion',
  mdaqs_immersiveness: 'Immersiveness',
  mdaqs_overall: 'Overall',
}
const MDAQS_ORDER = ['mdaqs_timbre', 'mdaqs_distortion', 'mdaqs_immersiveness', 'mdaqs_overall']

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

function formatReviewDate(value: string | null, verb = 'Reviewed'): string | null {
  if (!value) {
    return null
  }
  const ts = Date.parse(value)
  if (!Number.isFinite(ts)) {
    return null
  }
  const d = new Date(ts)
  return `${verb} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`
}

/** Verdict pill copy derived from the overall /10 score. */
function deriveVerdict(score10: number): string {
  if (score10 >= 8.5) {
    return 'Highly recommended'
  }
  if (score10 >= 7) {
    return 'Recommended'
  }
  if (score10 >= 5.5) {
    return 'Worth a look'
  }
  return 'Not recommended'
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (!parts.length) {
    return ''
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase()
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

/** Split a "- a\n- b" bulleted block (DealHunt stores pros/cons as one string). */
function splitBullets(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map(line => line.replace(/^[\s•*\-–—]+/, '').trim())
    .filter(Boolean)
}

/**
 * SoundGuys pros/cons as individual lines, capped. SoundGuys source ONLY — empty
 * when this product has no SoundGuys group.
 *
 * `prosBySource`/`consBySource` aggregate EVERY publication DealHunt has content
 * from (see productLine.ts: "those aggregate every source"). This block renders
 * under a SoundGuys heading with a SoundGuys byline, so taking any other source's
 * bullets here would attribute another publication's words to SoundGuys. Never
 * fall back to `groups[0]`.
 */
function contentList(groups: DealHuntContentGroup[] | undefined, max: number): string[] {
  const group = groups?.find(g => /soundguys/i.test(g.sourceName ?? ''))
  if (!group) {
    return []
  }
  const lines = group.items.flatMap(i => splitBullets(i.content))
  return lines.slice(0, max)
}

/** The SoundGuys ratings group — SoundGuys source ONLY (see the module comment). */
function soundGuysRatingGroup(
  enrichment: DealHuntProductLine['enrichment']
): DealHuntRatingGroup | undefined {
  // DealHunt's JSON: a null group or one without a ratings array is no SoundGuys group.
  return enrichment.ratingsBySource.find(
    g => Boolean(g) && Array.isArray(g.ratings) && /soundguys/i.test(g.sourceName ?? '')
  )
}

/**
 * One SoundGuys rating on the review block's /10 display scale, the first of `types` the
 * SoundGuys group carries (aliases: `['active_noise_cancelling', 'noise_cancelling']`). Same
 * normalisation as `buildSoundGuysReview`'s sub-scores, without its curated list or its cap of
 * six — the thread's scorecard asks for types that list leaves out (`microphone`). Null when
 * there is no SoundGuys group, no listed type, or a zero scale.
 */
export function soundGuysRating(
  enrichment: DealHuntProductLine['enrichment'],
  types: readonly string[]
): { key: string; score: number } | null {
  const group = soundGuysRatingGroup(enrichment)
  if (!group) {
    return null
  }
  for (const type of types) {
    const r = group.ratings.find(x => x?.ratingType === type)
    if (r && r.ratingScale) {
      return { key: type, score: round1((r.ratingValue / r.ratingScale) * 10) }
    }
  }
  return null
}

/**
 * Build the SoundGuys review model, or `null` when there is no SoundGuys ratings
 * group for this product (so the section can be omitted entirely). The one exception
 * is a sponsored ("Promoted") post DealHunt serves as the line's article: it carries
 * no score group, but the section still renders — labelled as promoted — so the
 * article is reachable from the product page.
 */
/** `maxSubScores`: the PDP's bars show six; the review's versus header wants every one. */
export function buildSoundGuysReview(
  enrichment: DealHuntProductLine['enrichment'],
  maxSubScores = 6
): SoundGuysReviewModel | null {
  const group = soundGuysRatingGroup(enrichment)
  const promoted = enrichment.soundguys?.promoted === true
  if (!group && !(promoted && enrichment.soundguys?.reviewUrl)) {
    return null
  }

  const byType = new Map((group?.ratings ?? []).map(r => [r.ratingType, r]))

  const overall = byType.get('overall')
  const score = overall ? round1((overall.ratingValue / overall.ratingScale) * 10) : null

  const subScores: ReviewSubScore[] = []
  const usedLabels = new Set<string>()
  for (const [type, label] of SUB_SCORE_LABELS) {
    if (usedLabels.has(label)) {
      continue
    }
    const r = byType.get(type)
    if (!r || !r.ratingScale) {
      continue
    }
    usedLabels.add(label)
    subScores.push({
      key: type,
      label,
      score: round1((r.ratingValue / r.ratingScale) * 10),
      pct: clampPct((r.ratingValue / r.ratingScale) * 100),
    })
    if (subScores.length >= maxSubScores) {
      break
    }
  }

  const mdaqs: MdaqsScore[] = MDAQS_ORDER.map(type => {
    const r = byType.get(type)
    if (!r || !r.ratingScale) {
      return null
    }
    return {
      key: type,
      label: MDAQS_LABELS[type] ?? type,
      score: round1(r.ratingValue),
      scale: r.ratingScale,
      pct: clampPct((r.ratingValue / r.ratingScale) * 100),
    }
  }).filter((m): m is MdaqsScore => m !== null)

  // Prefer the consolidated `enrichment.soundguys` (clean overview/pros/cons/authors);
  // fall back to the verdict entry + prosBySource/consBySource scraping when absent.
  const sg = enrichment.soundguys ?? null
  const verdict = (enrichment.verdicts ?? []).find(v => /soundguys/i.test(v.sourceName ?? ''))
  const reviewDate = sg?.publishedAt ?? group?.publishedAt ?? verdict?.publishedAt ?? null

  // DealHunt sends every credited author in `soundguys.authors`. Render them all;
  // fall back to the single byline on the verdict/content group when that's absent.
  const sgAuthors = sg?.authors ?? []
  const reviewers: ReviewAuthor[] = sgAuthors.length
    ? sgAuthors.map(a => ({
        name: a.name,
        initials: initialsOf(a.name),
        avatarUrl: a.avatarUrl,
        profileUrl: a.profileUrl,
        position: a.position,
      }))
    : (() => {
        const name = verdict?.authorName ?? group?.authorName
        return name
          ? [
              {
                name,
                initials: initialsOf(name),
                avatarUrl: group?.authorAvatarUrl ?? null,
                profileUrl: null,
                position: null,
              },
            ]
          : []
      })()

  return {
    score,
    verdictLabel: score !== null ? deriveVerdict(score) : null,
    reviewers,
    reviewDate: formatReviewDate(reviewDate, promoted ? 'Published' : 'Reviewed'),
    publishedAt: reviewDate,
    reviewUrl: sg?.reviewUrl ?? group?.reviewUrl ?? verdict?.reviewUrl ?? null,
    subScores,
    mdaqs,
    summary: sg?.overview ?? verdict?.content ?? null,
    pros: sg?.pros?.length ? sg.pros.slice(0, 6) : contentList(enrichment.prosBySource, 6),
    cons: sg?.cons?.length ? sg.cons.slice(0, 6) : contentList(enrichment.consBySource, 6),
    promoted,
    sponsorName: promoted ? sg?.sponsorName ?? null : null,
  }
}

function round1(n: number): number {
  return Math.round(n * 10) / 10
}

function clampPct(n: number): number {
  return Math.max(0, Math.min(100, n))
}

/** The price the PDP's hero shows (`ProductHero`), so the review and the PDP never disagree. */
export function linePrice(line: DealHuntProductLine): ReviewPrice | null {
  const { primaryProduct, aggregates } = line
  const cents = primaryProduct?.currentPriceCents ?? aggregates.bestPriceCents
  if (cents === null) {
    return null
  }
  return {
    cents,
    domain: primaryProduct?.domain ?? 'US',
    checkedAt: primaryProduct?.priceUpdatedAt ?? null,
  }
}

/**
 * A resolved product line as the review's "Compare these scores with" pins it: identity, product
 * page and lab key. Client-safe: the header's search builds one in the browser, the review mapper
 * at build time.
 */
export function reviewRival(line: DealHuntProductLine, trailingSlash: boolean): ReviewRival {
  return {
    nodeId: line.node.id,
    slug: line.node.fullSlug,
    name: productDisplayName(line),
    pdpHref: dealHuntProductHref(
      line.node.fullSlug,
      line.brandNode?.name ?? null,
      trailingSlash,
      line.brandNode?.slug ?? null
    ),
    clearbuyProductId: line.clearbuyProductId ?? null,
  }
}
